import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ReQcPanel } from "../../../components/ReQcPanel.js";
import { MORE_PROPOSALS_ACTION_KEY, PROJECT_ACTION_KEY, proposalActionKey, RE_ADJUSTABLE_FIELDS, RE_ERROR_TEXT, RE_GENERATION_MAX_SECONDS, RE_GENERATION_MIN_SECONDS, type ReGenerationLinkDto, type ReProjectViewDto, type ReProposalDto, type ReProposalSetDto } from "./re-api.js";

/**
 * FIX-011 / RE Stage 5 - the proposals of an RE project with an approved analysis: three from Grok (owner t782u; older projects keep their five), more on request; ALL batches are
 * shown and every proposal of a ready batch can be selected (no limit, across batches). A selected proposal can be adjusted in scene / outfit / environment only (each save is a new version; dialogue, voice,
 * music and effects stay Grok's), and is approved separately (approval 2 locks that version). Get JSON exports the approved plan. Stage 6: an approved proposal can be produced as a video (WAN 3.0), once per approved version.
 */
const fmt = (iso: string | null): string => (iso === null ? "—" : new Date(iso).toLocaleString());
const effective = (p: ReProposalDto, key: (typeof RE_ADJUSTABLE_FIELDS)[number]): string => p.adjustments[key] ?? (typeof p.content[key] === "string" ? (p.content[key] as string) : "");
const STATUS_LABEL: Record<ReProposalDto["status"], string> = { proposed: "not selected", draft: "selected — awaiting approval", approved: "approved (locked)", superseded: "replaced" };

function Dialogue({ value }: { readonly value: ReProposalDto["content"]["dialogue"] }) {
  if (value === undefined) return <span className="muted">—</span>;
  if (typeof value === "string") return <span>{value}</span>;
  return <ol style={{ margin: 0, paddingLeft: 18 }}>{value.map((l, i) => <li key={i}><strong>{l.speaker}:</strong> {l.text}</li>)}</ol>;
}

function Field({ label, children }: { readonly label: string; readonly children: React.ReactNode }) {
  return <div style={{ marginBottom: 6 }}><div className="muted" style={{ fontSize: 12 }}>{label}</div><div>{children}</div></div>;
}

type ReAct = (path: string, body?: unknown, key?: string) => Promise<boolean>;

function ProposalCard({ proposal, link, tenantId, canAct, busy, act: projectAct }: { readonly proposal: ReProposalDto; readonly link: ReGenerationLinkDto | null; readonly tenantId: string; readonly canAct: boolean; readonly busy: boolean; readonly act: ReAct }) {
  // FIX-064 RE UI lock: every request of this card runs under this proposal's own key — it never disables another card.
  const act = (path: string, body?: unknown): Promise<boolean> => projectAct(path, body, proposalActionKey(proposal.id));
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);
  const [producing, setProducing] = useState(false);
  const [seconds, setSeconds] = useState("");
  const secondsNumber = seconds.trim() === "" ? null : Number(seconds);
  const secondsValid = secondsNumber === null || (Number.isInteger(secondsNumber) && secondsNumber >= RE_GENERATION_MIN_SECONDS && secondsNumber <= RE_GENERATION_MAX_SECONDS);
  const value = (key: (typeof RE_ADJUSTABLE_FIELDS)[number]): string => draft[key] ?? effective(proposal, key);
  const changed = RE_ADJUSTABLE_FIELDS.filter((k) => value(k).trim() !== effective(proposal, k).trim() && value(k).trim() !== "");
  const c = proposal.content;
  return (
    <div className="card" data-testid="re-proposal" data-proposal={proposal.id} data-status={proposal.status} style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <strong data-testid="re-proposal-title">{c.title ?? `Proposal ${proposal.ordinal}`}</strong>
        <span className="muted" data-testid="re-proposal-status">#{proposal.ordinal} · version {proposal.revisionNo} · {STATUS_LABEL[proposal.status]}{proposal.status === "approved" ? ` · ${fmt(proposal.approvedAt)}` : ""}</span>
      </div>

      {proposal.status === "draft" && canAct ? (
        <div data-testid="re-adjust" style={{ marginTop: 6 }}>
          <p className="muted" style={{ margin: "0 0 4px" }}>You can adjust the scene, outfit and environment. Dialogue, voice, music and effects stay as Grok proposed.</p>
          {RE_ADJUSTABLE_FIELDS.map((k) => (
            <label key={k} style={{ display: "block", marginBottom: 6 }}>{k[0]!.toUpperCase() + k.slice(1)}
              <textarea data-testid={`re-adjust-${k}`} rows={2} maxLength={2000} style={{ width: "100%" }} value={value(k)} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} />
            </label>
          ))}
          <button type="button" data-testid="re-save-adjustments" disabled={busy || changed.length === 0}
            onClick={() => void act(`/proposals/${encodeURIComponent(proposal.id)}/adjust`, Object.fromEntries(changed.map((k) => [k, value(k).trim()]))).then((ok) => { if (ok) setDraft({}); })}>Save adjustments (new version)</button>
        </div>
      ) : (
        <>
          <Field label="Scene">{effective(proposal, "scene") || <span className="muted">—</span>}</Field>
          <Field label="Outfit">{effective(proposal, "outfit") || <span className="muted">—</span>}</Field>
          <Field label="Environment">{effective(proposal, "environment") || <span className="muted">—</span>}</Field>
        </>
      )}
      <Field label="Dialogue"><Dialogue value={c.dialogue} /></Field>
      <Field label="Voice">{c.voice ?? "—"}</Field>
      <Field label="Music">{c.music ?? "—"}</Field>
      <Field label="Effects">{c.effects ?? "—"}</Field>
      {Object.keys(proposal.adjustments).length > 0 ? <p className="muted" data-testid="re-proposal-adjusted">Adjusted by you: {Object.keys(proposal.adjustments).join(", ")}.</p> : null}

      {canAct && proposal.status === "proposed" ? (
        <button type="button" data-testid="re-select" disabled={busy}
          onClick={() => void act("/proposals/select", { proposalIds: [proposal.id] })}>Select for production</button>
      ) : null}
      {canAct && proposal.status === "draft" ? (
        !confirming ? <button type="button" data-testid="re-approve-proposal" disabled={busy} style={{ marginLeft: 8 }} onClick={() => setConfirming(true)}>Approve this proposal</button> : (
          <p data-testid="re-approve-proposal-confirm">
            Approving locks version {proposal.revisionNo} exactly as shown above (including your adjustments). Nothing is produced yet.{" "}
            <button type="button" data-testid="re-approve-proposal-yes" disabled={busy} onClick={() => void act(`/proposals/${encodeURIComponent(proposal.id)}/approve`).then(() => setConfirming(false))}>Yes, approve</button>{" "}
            <button type="button" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
          </p>
        )
      ) : null}
      {proposal.status === "approved" ? (
        link !== null ? (
          <>
          <p data-testid="re-video-created" style={{ marginBottom: 0 }}>A video was created from this version. <Link data-testid="re-video-link" to={`/generate/history/${encodeURIComponent(link.videoId)}`}>Open it</Link> — it continues through the normal review path.</p>
          <ReQcPanel tenantId={tenantId} videoId={link.videoId} />
          </>
        ) : canAct ? (
          !producing ? <button type="button" data-testid="re-create-video" disabled={busy} onClick={() => setProducing(true)}>Create video</button> : (
            <div data-testid="re-create-video-confirm">
              <p style={{ margin: "0 0 6px" }}>This sends exactly version {proposal.revisionNo} (with your adjustments), the persona's images and its voice sample to the video generator. It is a paid generation, created once for this version.</p>
              <label>Length in seconds (optional, {RE_GENERATION_MIN_SECONDS}–{RE_GENERATION_MAX_SECONDS}; default: the source length){" "}
                <input data-testid="re-video-seconds" type="number" min={RE_GENERATION_MIN_SECONDS} max={RE_GENERATION_MAX_SECONDS} step={1} value={seconds} onChange={(e) => setSeconds(e.target.value)} style={{ width: 70 }} />
              </label>{" "}
              <button type="button" data-testid="re-create-video-yes" disabled={busy || !secondsValid}
                onClick={() => void act(`/proposals/${encodeURIComponent(proposal.id)}/generate`, secondsNumber === null ? {} : { durationSeconds: secondsNumber }).then(() => setProducing(false))}>Yes, create the video</button>{" "}
              <button type="button" disabled={busy} onClick={() => setProducing(false)}>Cancel</button>
            </div>
          )
        ) : null
      ) : null}
    </div>
  );
}

export function ReProposals({ view, tenantId, canAct, isPending, act }: { readonly view: ReProjectViewDto; readonly tenantId: string; readonly canAct: boolean; readonly isPending: (key: string) => boolean; readonly act: ReAct }) {
  const busy = isPending(PROJECT_ACTION_KEY);
  const moreBusy = isPending(MORE_PROPOSALS_ACTION_KEY);
  const sets = view.proposalSets ?? [];
  const proposals = view.proposals ?? [];
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [jsonBusy, setJsonBusy] = useState(false);

  const latest = useMemo(() => {
    const m = new Map<string, ReProposalDto>();
    for (const p of proposals) {
      const k = `${p.setId}:${p.ordinal}`;
      const cur = m.get(k);
      if (cur === undefined || p.revisionNo > cur.revisionNo) m.set(k, p);
    }
    return [...m.values()];
  }, [proposals]);
  const selectedCount = latest.filter((p) => p.status === "draft" || p.status === "approved").length;
  const approvedCount = proposals.filter((p) => p.status === "approved").length;
  const readySets = sets.filter((s) => s.status === "ready").sort((a, b) => a.batchNo - b.batchNo);
  const pending = sets.find((s) => s.status === "requested" || s.status === "awaiting_callback") ?? null;
  const batches = [...new Set(sets.map((s) => s.batchNo))].sort((a, b) => a - b);
  const brokenBatch = (batchNo: number): ReProposalSetDto | null => {
    const attempts = sets.filter((s) => s.batchNo === batchNo);
    return attempts.some((s) => s.status === "ready" || s.status === "requested" || s.status === "awaiting_callback") ? null : (attempts[attempts.length - 1] ?? null);
  };
  const broken = batches.map(brokenBatch).find((x) => x !== null) ?? null;
  const firstBatchReady = readySets.some((s) => s.batchNo === 1);

  async function getJson(): Promise<void> {
    setJsonBusy(true);
    setJsonError(null);
    try {
      const response = await fetch(`/re/${tenantId}/projects/${encodeURIComponent(view.project.id)}/plan-json`, { credentials: "include" });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { error?: string; message?: string } | null;
        setJsonError((body?.error !== undefined ? RE_ERROR_TEXT[body.error] : undefined) ?? body?.message ?? "Could not export the plan.");
        return;
      }
      const blob = await response.blob();
      const name = /filename="([^"]+)"/.exec(response.headers.get("content-disposition") ?? "")?.[1] ?? "re-plan.json";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setJsonError("Could not export the plan.");
    } finally {
      setJsonBusy(false);
    }
  }

  const requestFirst = view.project.state === "ANALYSIS_APPROVED" || view.project.state === "PROPOSALS_FAILED" || view.project.state === "PROPOSALS_TIMED_OUT";
  return (
    <section data-testid="re-proposals" style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0 }}>Reconstruction proposals</h3>
        <span>
          <button type="button" data-testid="re-get-json" disabled={approvedCount === 0 || jsonBusy} title={approvedCount === 0 ? "Approve at least one proposal first" : "Download the approved plan (re-plan-v1)"} onClick={() => void getJson()}>{jsonBusy ? "Preparing…" : "Get JSON"}</button>
        </span>
      </div>
      {jsonError !== null ? <div className="error-banner" role="alert" data-testid="re-json-error">{jsonError}</div> : null}
      <p className="muted">Nothing is produced in this step. Select any proposals from any batch, adjust their scene / outfit / environment if you like, then approve each one separately. <span data-testid="re-counts">Selected {selectedCount} · approved {approvedCount}</span></p>

      {requestFirst && pending === null ? (
        <div className="card" data-testid="re-proposals-start">
          {view.project.state === "ANALYSIS_APPROVED" ? <p style={{ marginTop: 0 }}>The analysis is approved. Ask Grok for three reconstruction proposals.</p> : <p style={{ marginTop: 0 }}><strong>{view.project.state === "PROPOSALS_TIMED_OUT" ? "The proposals did not come back in time." : "The proposals request failed."}</strong> Nothing is retried automatically.</p>}
          {canAct ? <button type="button" data-testid="re-request-proposals" disabled={busy} onClick={() => void act("/proposals", {})}>{view.project.state === "ANALYSIS_APPROVED" ? "Get 3 proposals" : "Try again"}</button> : <p className="muted">Your role cannot request proposals.</p>}
        </div>
      ) : null}

      {pending !== null ? (
        <div className="card" data-testid="re-proposals-in-flight">
          <p style={{ margin: 0 }}>Grok is preparing {pending.batchNo === 1 ? "the first three proposals" : `more proposals (batch ${pending.batchNo})`}… <span className="muted">started {fmt(pending.createdAt)} · gives up at {fmt(pending.deadlineAt)} · this page updates by itself.</span></p>
        </div>
      ) : null}

      {readySets.map((set) => (
        <div key={set.id} data-testid="re-batch" data-batch={set.batchNo} style={{ marginTop: 12 }}>
          <h4 style={{ marginBottom: 6 }}>{set.batchNo === 1 ? "First proposals" : `More proposals — batch ${set.batchNo}`}</h4>
          {latest.filter((p) => p.setId === set.id).sort((a, b) => a.ordinal - b.ordinal).map((p) => <ProposalCard key={p.id} proposal={p} link={(view.generationLinks ?? []).find((l) => l.proposalId === p.id) ?? null} tenantId={tenantId} canAct={canAct} busy={isPending(proposalActionKey(p.id))} act={act} />)}
        </div>
      ))}

      {firstBatchReady && broken !== null && pending === null ? (
        <p className="muted" data-testid="re-batch-failed">The last request for more proposals did not complete ({broken.status === "timed_out" ? "timed out" : "failed"}). Nothing is retried automatically.</p>
      ) : null}
      {firstBatchReady && pending === null && canAct ? (
        <p><button type="button" data-testid="re-more-proposals" disabled={moreBusy} onClick={() => void act("/proposals", { more: true }, MORE_PROPOSALS_ACTION_KEY)}>{broken !== null ? "Try again for more proposals" : "Request more proposals"}</button></p>
      ) : null}
    </section>
  );
}

